import React, { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { http } from "../context/AuthContext";
import { BRAND, CATEGORIES } from "../constants/brand";

export default function VendorRegister() {
  const [form, setForm] = useState({ shop_name: "", owner_name: "", email: "", phone: "", city: "Delhi NCR", address: "", categories: [] });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const toggleCat = (k) =>
    setForm((f) => ({ ...f, categories: f.categories.includes(k) ? f.categories.filter((x) => x !== k) : [...f.categories, k] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.shop_name || !form.owner_name || !form.email || !form.phone) { toast.error("Please fill required fields"); return; }
    setBusy(true);
    try {
      await http.post("/vendor/register", form);
      setDone(true);
      toast.success("Application received — we'll verify within 24 hours");
    } catch { toast.error("Submission failed — please try WhatsApp"); }
    finally { setBusy(false); }
  };

  if (done) {
    return (
      <section className="min-h-[70vh]">
        <div className="mx-auto max-w-2xl px-4 py-24 text-center md:px-6">
          <CheckCircle2 size={64} className="mx-auto text-lime-500" />
          <h1 className="mt-6 font-display text-4xl font-bold" data-testid="vendor-reg-success">Application received!</h1>
          <p className="mt-3 text-neutral-600">Our team will verify your shop within 24 hours. We'll reach out on WhatsApp at the number you provided.</p>
          <a href={BRAND.social.whatsapp} target="_blank" rel="noopener noreferrer" className="mt-6 inline-block rounded-md bg-lime-400 px-5 py-3 text-sm font-bold text-neutral-950 hover:bg-lime-300" data-testid="vendor-reg-whatsapp">Message us on WhatsApp</a>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="border-b border-neutral-200 bg-neutral-950 text-white grain">
        <div className="mx-auto max-w-7xl px-4 py-20 md:px-6 md:py-28">
          <span className="text-xs font-semibold uppercase tracking-widest text-lime-400">For print shops</span>
          <h1 className="mt-3 max-w-3xl font-display text-4xl font-black leading-tight tracking-tight md:text-6xl" data-testid="vendor-reg-title">Register your shop <span className="text-lime-400">for free.</span></h1>
          <p className="mt-6 max-w-2xl text-neutral-300">Get verified in 24 hours. Start receiving pre-paid, escrow-safe orders on WhatsApp.</p>
        </div>
      </section>

      <section className="border-b border-neutral-200">
        <form onSubmit={submit} className="mx-auto max-w-3xl px-4 py-16 md:px-6 md:py-24" data-testid="vendor-reg-form">
          <div className="grid gap-4 md:grid-cols-2">
            <F label="Shop name*" value={form.shop_name} onChange={(v) => setForm({ ...form, shop_name: v })} testid="vreg-shop" />
            <F label="Owner name*" value={form.owner_name} onChange={(v) => setForm({ ...form, owner_name: v })} testid="vreg-owner" />
            <F label="Email*" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="vreg-email" />
            <F label="Phone / WhatsApp*" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="vreg-phone" />
            <div className="md:col-span-2">
              <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">City</label>
              <select value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1 w-full rounded-md border border-neutral-300 px-3 py-3 text-sm" data-testid="vreg-city">
                <option>Delhi NCR</option><option>Noida</option><option>Gurugram</option><option>Faridabad</option><option>Ghaziabad</option><option>Other</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <F label="Shop address (optional)" value={form.address} onChange={(v) => setForm({ ...form, address: v })} testid="vreg-address" />
            </div>
          </div>

          <div className="mt-6">
            <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">What do you print? (pick as many)</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {CATEGORIES.map((c) => (
                <button type="button" key={c.key} onClick={() => toggleCat(c.key)} className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${form.categories.includes(c.key) ? "border-neutral-900 bg-lime-400 text-neutral-950" : "border-neutral-300 hover:border-neutral-900"}`} data-testid={`vreg-cat-${c.key}`}>{c.label}</button>
              ))}
            </div>
          </div>

          <button type="submit" disabled={busy} className="btn-offset mt-8 rounded-md bg-neutral-950 px-6 py-3 text-sm font-bold text-white hover:bg-neutral-800 disabled:opacity-60" data-testid="vreg-submit">
            {busy ? "Submitting…" : "Register my shop"}
          </button>
        </form>
      </section>
    </>
  );
}

function F({ label, testid, onChange, ...props }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">{label}</label>
      <input {...props} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full rounded-md border border-neutral-300 px-3 py-3 text-sm focus:border-neutral-900 focus:outline-none focus:ring-2 focus:ring-lime-400" data-testid={testid} />
    </div>
  );
}
